<div class="projects-page">
  <app-header
    [title]="'TITLES.PROJECTS' | translate"
    [subtitle]="'COMPONENTS.PROJECTS.SUBTITLE' | translate"
  >
    <div header-actions class="projects-header-actions">
      <app-navbar-profile-info />
    </div>
  </app-header>

  <div class="projects-content py-6 md:py-8 relative z-10">
    <div class="projects-controls" [formGroup]="filterForm.form">
      @if (canCreateProject$ | async) {
        <button type="button" class="add-project-button" (click)="openCreateProjectModal()">
          + {{ "BUTTONS.ADD_PROJECT" | translate }}
        </button>
      }

      <label for="isArchived" class="archive-filter-label">
        <input type="checkbox" formControlName="isArchived" name="isArchived" id="isArchived" />
        <span>{{ "FILTERS.ARCHIVED" | translate }}</span>
      </label>
    </div>

    @if (!(uiProjects$ | async)?.length) {
      <div class="empty-state-wrapper">
        <app-empty-content
          [showImage]="true"
          [message]="'MESSAGES.EMPTY_LIST' | translate: { param: emptyProjectListParam[currentLang] }"
          [tooltip]="'TOOLTIPS.EMPTY_PROJECT_LIST' | translate"
          [textPositionClass]="'text-center'"
        />
      </div>
    } @else {
      <div class="projects-grid">
        @for (project of uiProjects$ | async; track project.id) {
          <article class="project-card" [class.project-card-archived]="project.isArchived">
            <header class="project-card-header">
              <div class="project-card-title-block">
                <a [routerLink]="project.id" class="project-card-title">{{ project.title }}</a>
                @if (project.tagLabel) {
                  <span class="project-tag" [ngClass]="project.tagClass">
                    {{ project.tagLabel }}
                  </span>
                }
              </div>

              @if (canEditProject$ | async) {
                <button
                  type="button"
                  [ngClass]="project.archiveButtonClass"
                  (click)="project.isArchived ? onUnarchive(project.id) : onArchive(project.id)"
                >
                  {{ (project.isArchived ? "BUTTONS.UNARCHIVE" : "BUTTONS.ARCHIVE") | translate }}
                </button>
              }
            </header>

            @if (project.description) {
              <p class="project-description">{{ project.description }}</p>
            }

            <section class="status-tiles-grid">
              <div class="status-tile status-tile-completed">
                <p class="status-tile-count">{{ project.completedAssignmentsCount }}</p>
                <p class="status-tile-label">{{ "ASSIGNMENT_STATUSES_PLURAL.COMPLETED" | translate }}</p>
              </div>
              <div class="status-tile status-tile-active">
                <p class="status-tile-count">{{ project.activeAssignmentsCount }}</p>
                <p class="status-tile-label">{{ "ASSIGNMENT_STATUSES_PLURAL.ACTIVE" | translate }}</p>
              </div>
              <div class="status-tile status-tile-overdue">
                <p class="status-tile-count">{{ project.overdueAssignmentsCount }}</p>
                <p class="status-tile-label">{{ "ASSIGNMENT_STATUSES_PLURAL.OVERDUE" | translate }}</p>
              </div>
              <div class="status-tile status-tile-new">
                <p class="status-tile-count">{{ project.newAssignmentsCount }}</p>
                <p class="status-tile-label">{{ "ASSIGNMENT_STATUSES_PLURAL.NEW" | translate }}</p>
              </div>
              <div class="status-tile status-tile-postponed">
                <p class="status-tile-count">{{ project.postponedAssignmentsCount }}</p>
                <p class="status-tile-label">{{ "ASSIGNMENT_STATUSES_PLURAL.POSTPONED" | translate }}</p>
              </div>
            </section>

            <section class="card-section">
              <p class="card-section-title">{{ "COMPONENTS.PROJECTS.ACTIVE_TASKS" | translate }}</p>

              @if (!project.hasVisibleAssignments) {
                <p class="card-section-empty">{{ "MESSAGES.EMPTY_LIST" | translate: { param: emptyProjectListParam[currentLang] } }}</p>
              } @else {
                <div class="task-list">
                  @for (assignment of project.visibleAssignments; track assignment.id) {
                    <a
                      [routerLink]="[project.id, 'assignments', assignment.id]"
                      class="task-item"
                      [style.borderLeftColor]="assignment.borderColorHex"
                    >
                      <div class="task-item-header">
                        <p class="task-title">{{ assignment.title }}</p>
                        <span [ngClass]="assignment.badgeClass">
                          {{ assignment.statusLabelKey | translate }}
                        </span>
                      </div>
                      <p class="task-assignee">
                        <span class="task-assignee-label">{{ "COMPONENTS.PROJECTS.ASSIGNEE" | translate }}:</span>
                        {{ assignment.employeeName || ("COMPONENTS.PROJECTS.UNASSIGNED" | translate) }}
                      </p>
                      <p class="task-meta">
                        {{ assignment.deadline ? (assignment.deadline | date: uaDateFormat) : ("ASSIGNMENT_INFO.DEADLINE" | translate) }}
                      </p>
                    </a>
                  }
                </div>

                @if (project.canExpandAssignments) {
                  <button type="button" class="expand-button" (click)="toggleTaskExpanded(project.id)">
                    @if (project.isTaskExpanded) {
                      {{ "BUTTONS.SHOW_LESS" | translate }}
                    } @else {
                      {{ "BUTTONS.WHOLE_LIST" | translate }} ({{ project.hiddenAssignmentsCount }})
                    }
                  </button>
                }
              }
            </section>

            <section class="card-section team-section">
              <p class="card-section-title">{{ "TABLE_HEADERS.TEAM" | translate }}</p>

              @if (project.hasTeamMembers) {
                @if (!project.isTeamExpanded) {
                  <div class="team-row">
                    <div class="team-avatars">
                      @for (member of project.previewTeamMembers; track member.id) {
                        <span class="member-avatar" [class.member-avatar--placeholder]="!member.hasImage" [title]="member.fullName">
                          @if (member.hasImage) {
                            <img [src]="member.imageUrl" [alt]="member.fullName" />
                          } @else {
                            {{ member.initials }}
                          }
                        </span>
                      }
                    </div>

                    @if (project.canExpandTeam) {
                      <button type="button" class="expand-button team-expand-button" (click)="toggleTeamExpanded(project.id)">
                        {{ "BUTTONS.WHOLE_LIST" | translate }} ({{ project.teamMembersCount }})
                      </button>
                    }
                  </div>
                } @else {
                  <div class="team-expanded">
                    @if (project.hasExpandedTeamMembers) {
                      <div class="team-full-list team-full-list-scroll">
                        @for (member of project.fullTeamMembers; track member.id) {
                          <div class="team-member-row" [title]="member.fullName">
                            <span
                              class="member-avatar member-avatar-full"
                              [class.member-avatar--placeholder]="!member.hasImage"
                            >
                              @if (member.hasImage) {
                                <img [src]="member.imageUrl" [alt]="member.fullName" />
                              } @else {
                                {{ member.initials }}
                              }
                            </span>
                            <span class="team-member-name">{{ member.fullName }}</span>
                          </div>
                        }
                      </div>
                    }

                    <button type="button" class="expand-button team-collapse-button" (click)="toggleTeamExpanded(project.id)">
                      {{ "BUTTONS.SHOW_LESS" | translate }}
                    </button>
                  </div>
                }
              } @else {
                <p class="card-section-empty">{{ "MESSAGES.EMPTY_LIST" | translate: { param: emptyProjectListParam[currentLang] } }}</p>
              }
            </section>

            <footer class="project-card-footer">
              <a [routerLink]="project.id" class="open-project-button">{{ "BUTTONS.OPEN_PROJECT" | translate }}</a>
            </footer>
          </article>
        }
      </div>
    }
    <app-reusable-modal
      [isOpen]="isCreateProjectModalOpen"
      [panelClass]="'max-w-5xl projects-create-modal-panel'"
      [contentScoped]="true"
      [title]="'TITLES.ADD_PROJECT' | translate"
      [subtitle]="'COMPONENTS.PROJECTS.CREATE_MODAL.SUBTITLE' | translate"
      (closed)="closeCreateProjectModal()"
    >
      <app-create-project
        [companyId]="companyId"
        (cancelled)="closeCreateProjectModal()"
        (created)="onCreateProjectSuccess()"
      />
    </app-reusable-modal>
  </div>
</div>
